'use client';

/**
 * AstorWidgetBubble Component
 *
 * Floating Action Button (FAB) in the bottom-right corner that serves as the
 * primary entry point for Astor AI interactions. Features a subtle pulse animation
 * when nudges are available, with full reduced-motion support.
 *
 * @see docs/plans/2026-02-20-DESIGN-astor-ai-native-onboarding-help.md
 */

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { alpha, Box, useTheme } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';

import { useI18n } from '../../hooks/useI18n';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { glassEffects } from '../../theme/shadows';

// ============================================================================
// TYPES
// ============================================================================

export interface AstorWidgetBubbleProps {
  /** Click handler to toggle the widget open/close */
  onClick: () => void;
  /** Whether there is a nudge available to show */
  hasNudge?: boolean;
  /** Whether the widget is currently open */
  isOpen?: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const BUBBLE_SIZE = 56;
const PULSE_INTERVAL_MS = 60_000;
const PULSE_DURATION_S = 2;

// ============================================================================
// COMPONENT
// ============================================================================

export function AstorWidgetBubble({
  onClick,
  hasNudge = false,
  isOpen = false,
}: AstorWidgetBubbleProps): React.JSX.Element {
  const theme = useTheme();
  const { t } = useI18n();
  const prefersReducedMotion = useReducedMotion();
  const isDark = theme.palette.mode === 'dark';
  const glass = isDark ? glassEffects.dark : glassEffects.light;

  // Pulse state: triggers a pulse animation every 60s when a nudge is available
  const [shouldPulse, setShouldPulse] = useState(false);

  useEffect(() => {
    if (!hasNudge || prefersReducedMotion) {
      setShouldPulse(false);
      return;
    }

    // Pulse immediately when nudge appears
    setShouldPulse(true);
    const timeout = setTimeout(() => setShouldPulse(false), PULSE_DURATION_S * 1000);

    // Set up periodic pulse
    const interval = setInterval(() => {
      setShouldPulse(true);
      setTimeout(() => setShouldPulse(false), PULSE_DURATION_S * 1000);
    }, PULSE_INTERVAL_MS);

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [hasNudge, prefersReducedMotion]);

  return (
    <Box
      sx={{
        position: 'relative',
      }}
    >
      {/* Pulse ring (behind the bubble) */}
      <AnimatePresence>
        {shouldPulse && !prefersReducedMotion && (
          <motion.div
            initial={{ scale: 1, opacity: 0.6 }}
            animate={{ scale: 2.2, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: PULSE_DURATION_S, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: BUBBLE_SIZE,
              height: BUBBLE_SIZE,
              borderRadius: '50%',
              border: `2px solid ${theme.palette.primary.main}`,
              pointerEvents: 'none',
            }}
          />
        )}
      </AnimatePresence>

      {/* Nudge indicator dot */}
      {hasNudge && (
        <Box
          sx={{
            position: 'absolute',
            top: 2,
            right: 2,
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: 'error.main',
            border: `2px solid ${theme.palette.background.paper}`,
            zIndex: 1,
          }}
        />
      )}

      {/* Main bubble button */}
      <Box
        component={prefersReducedMotion ? 'button' : motion.button}
        {...(!prefersReducedMotion && {
          whileHover: { scale: 1.08 },
          whileTap: { scale: 0.95 },
        })}
        onClick={onClick}
        aria-label={isOpen ? t('a11y.closeAstor') : t('a11y.openAstor')}
        aria-expanded={isOpen}
        role="button"
        tabIndex={0}
        sx={{
          width: BUBBLE_SIZE,
          height: BUBBLE_SIZE,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          border: `1px solid ${glass.border}`,
          background: glass.background,
          backdropFilter: glass.backdropBlur,
          WebkitBackdropFilter: glass.backdropBlur,
          boxShadow: glass.shadow,
          transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
          outline: 'none',
          padding: 0,
          // Reset button styles
          fontFamily: 'inherit',
          fontSize: 'inherit',
          '&:hover': {
            borderColor: glass.borderHover,
            boxShadow: glass.shadowHover,
          },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        }}
      >
        <AutoAwesomeIcon
          sx={{
            fontSize: 28, // Off-scale: between lg (24) and xl (32)
            color: isOpen
              ? theme.palette.primary.main
              : isDark
                ? alpha(theme.palette.common.white, 0.85)
                : alpha(theme.palette.common.black, 0.7),
            transition: 'color 0.2s ease',
          }}
        />
      </Box>
    </Box>
  );
}

AstorWidgetBubble.displayName = 'AstorWidgetBubble';

export default AstorWidgetBubble;
